import { useShoppingCart } from "../../providers/ShoppingCartProvider";
import React, { useEffect, useMemo, useState } from "react";

import IconButton from "@material-ui/core/IconButton";
import Input from "@material-ui/core/Input";
import makeStyles from "@material-ui/core/styles/makeStyles";
import styled from "styled-components";
import useDebounce from "../../../hooks/useDebounce";
import DeleteIcon from "@material-ui/icons/Delete";
import DecreaseIcon from "@material-ui/icons/RemoveCircle";
import IncreaseIcon from "@material-ui/icons/AddCircle";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import DrawTemplate from "../../../components/Template/DrawTemplate";
import ListItemText from "@material-ui/core/ListItemText";
import EpmsImage from "../../../components/Common/EpmsImage";
import useTemplate from "../../../hooks/useTemplate";
import { usePriceList } from "../../providers/PriceListProvider";
import { createInstance } from "../../../Providers/TemplateInstanceProvider";

const OrderLineMetaContainer = styled.div`
  display: flex;
  max-width: 100%;
  align-items: center;
`;

const ProductInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const ProductName = styled.div`
  font-size: 14px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  min-width: 0;
  font-weight: 500;
`;

const ProductDescription = styled.div`
  color: #757575;
  font-size: 12px;
`;

const PriceContainer = styled.div`
  padding-top: 18px;
  flex: 0 0 auto;
`;

const ButtonContainer = styled.div`
  flex: 0 0 auto;
`;

const ChangeQuantityContainer = styled.div`
  display: flex;
  flex-grow: 0;
`;

const useStyles = makeStyles((theme) => ({
  changeQuantityInput: {
    width: "40px",
    "& input": {
      textAlign: "center",
    },
  },
  previewList: {
    "& .template-content": {
      boxShadow: "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
    },
  },
  templateContainer: {
    width: "55px",
    height: "55px",
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.primary.light,
    marginRight: theme.spacing(2),
  },
}));

const ChangeQuantity = ({ orderLine }) => {
  const classes = useStyles();
  const { updateItem, deleteItem } = useShoppingCart();
  const [dirty, setDirty] = useState(false);
  const [quantity, setQuantity] = useState(parseInt(orderLine.quantity));
  const debouncedQuantity = useDebounce(quantity, 200);

  useEffect(() => {
    if (!dirty) {
      return;
    }
    updateItem(orderLine, quantity);
    setDirty(false);
  }, [debouncedQuantity]);

  // If the server quantity change, update
  useEffect(() => {
    setQuantity(parseInt(orderLine.quantity));
    setDirty(false);
  }, [orderLine.quantity]);

  const increaseQuantity = () => {
    setQuantity(quantity + 1);
    setDirty(true);
  };

  const decreaseQuantity = () => {
    if (quantity > orderLine.minimumQuantity) {
      setQuantity(quantity - 1);
      setDirty(true);
    }
  };

  const doDelete = () => {
    deleteItem(orderLine);
  };

  return (
    <ChangeQuantityContainer>
      {orderLine.quantity == orderLine.minimumQuantity ? (
        <IconButton color="primary" onClick={doDelete}>
          <DeleteIcon />
        </IconButton>
      ) : (
        <IconButton color="primary" onClick={decreaseQuantity}>
          <DecreaseIcon />
        </IconButton>
      )}
      <Input
        className={classes.changeQuantityInput}
        type="number"
        value={quantity}
      />
      <IconButton color="primary" onClick={increaseQuantity}>
        <IncreaseIcon />
      </IconButton>
    </ChangeQuantityContainer>
  );
};

const OrderLinePreview = ({ orderLine }) => {
  const classes = useStyles();
  console.log("Order line", orderLine);

  if (orderLine.templates) {
    return (
      <List className={classes.previewList}>
        {orderLine.templates.map((templateInfo, index) => {
          console.log("Template info", templateInfo);
          return <TemplatePreview key={index} templateInfo={templateInfo} />;
        })}
      </List>
    );
  } else if (orderLine.digitalFiles) {
    console.log("Digital files!", orderLine.digitalFiles);
    return (
      <ImageList>
        {orderLine.digitalFiles.map((img) => {
          return (
            <li>
              <EpmsImage uuid={img.uuid} size={160} />
            </li>
          );
        })}
      </ImageList>
    );
  }
};

const TemplatePreview = ({ templateInfo }) => {
  const { templateUuid, userResources, productUuid, year } = templateInfo;
  const classes = useStyles();

  const [template, isLoading] = useTemplate(templateUuid);
  const { getProductByUuid } = usePriceList();

  const product = useMemo(() => {
    return getProductByUuid(productUuid);
  }, [productUuid]);

  const templateInstance = useMemo(() => {
    if (!template) {
      return null;
    }

    const inst = createInstance(template);
    return {
      ...inst,
      resources: {
        ...inst.resources,
        ...userResources,
      },
    };
  }, [template]);

  if (!product || !templateInstance) {
    console.log("Render preview", product, templateInstance);
    return null;
  }

  console.log("Rendering preview", templateInstance);

  return (
    <ListItem disableGutters={true}>
      <div className={classes.templateContainer}>
        <DrawTemplate
          templateInstance={templateInstance}
          containerSize={{ width: 50, height: 50 }}
        />
      </div>
      <ListItemText primary={product.name} secondary={templateInstance.name} />
    </ListItem>
  );
};

export {
  ChangeQuantity,
  OrderLinePreview,
  ProductInfo,
  ProductName,
  ProductDescription,
  OrderLineMetaContainer,
  PriceContainer,
  ButtonContainer,
};
